import { DeviceLastReadingDto, Port } from '@thingslog/repositories';
import React, { useEffect, useState, FC } from 'react';
import { SensorDisplayManager, SensorInfo } from '../../../utils';
import { format } from 'date-fns';
import { IconButton, Typography } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import clsx from 'clsx';
import { SensorDisplay } from './SensorDisplay';
import { Panel, PanelTitle } from './base';
import { useTranslation } from 'react-i18next';
import { cn } from '@thingslog/ui-components';

export const SensorSelector: FC<SensorSelectorProps> = ({
  onRefreshBtnClick,
  ports,
  readings,
  selectedSensorIndex,
  readingsFetchedAt,
  onSensorClick,
  className,
  isDisabled,
}: SensorSelectorProps) => {
  const [sensors, setSensors] = useState<SensorInfo[]>([]);
  const { t } = useTranslation();

  useEffect(() => {
    setSensors(
      SensorDisplayManager.getSensorInfo({
        ports,
        readings,
        exclude: { sensorTypes: ['on_off_auto_output_sensor'] },
      })
    );
  }, [ports, readings]);

  return (
    <Panel className={clsx('space-y-2 !px-3 !py-2 md:!px-3 md:!py-3', className)}>
      <div className="flex justify-between items-center">
        <PanelTitle title={t('sensor_all_info_header')} />
        <div className="flex items-center justify-center gap-0.5">
          {readingsFetchedAt && (
            <Typography variant="body2">{format(readingsFetchedAt, 'HH:mm:ss')}</Typography>
          )}

          {onRefreshBtnClick && (
            <IconButton
              size="small"
              onClick={(): void => {
                onRefreshBtnClick();
              }}
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          )}
        </div>
      </div>
      <div
        className={cn('grid justify-start gap-2')}
        style={{
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 7.75rem), 7.75rem))',
        }}
      >
        {sensors.map((sensor: SensorInfo) => {
          const isSelected = selectedSensorIndex === sensor.index;
          return (
            <SensorDisplay
              key={sensor.index}
              isSelected={isSelected}
              onClick={(): void => {
                onSensorClick && onSensorClick(isSelected ? null : sensor);
              }}
              name={sensor.name}
              label={sensor.label}
              isBlocked={isDisabled === true}
            />
          );
        })}
      </div>
    </Panel>
  );
};

interface SensorSelectorProps {
  selectedSensorIndex: number | null;
  ports?: Record<number, Port>;
  readings?: DeviceLastReadingDto[];
  readingsFetchedAt?: number;
  onSensorClick?: (sensor: SensorInfo | null) => void;
  onRefreshBtnClick?: () => void;
  isDisabled?: boolean;
  className?: string;
}
